<script lang="ts">
	import type { BoundaryCoordinates } from '$lib/components/map/types';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import { areBoundsEqual } from '$lib/components/map/map-utils/areBoundsEqual';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import * as m from '$paraglide/messages';
	import maplibre from 'maplibre-gl';
	import { onDestroy, onMount } from 'svelte';
	import { getMapContext } from 'svelte-maplibre';

	const { LngLatBounds } = maplibre;

	type RefreshType = 'search' | 'pois-hrt';

	interface Props {
		mapBounds: BoundaryCoordinates;
		type?: RefreshType;
		onRefreshBounds?: (bounds: BoundaryCoordinates) => void;
	}

	const { mapBounds, type = 'search', onRefreshBounds }: Props = $props();

	const { map } = $derived(getMapContext());
	const { isRefreshButtonVisible } = getMapLayoutContext();

	const initialFittedBounds = map?.getBounds().toArray() as BoundaryCoordinates | undefined;

	/**
	 * This function computes the percentage of change between two segments on a 1D axis.
	 * Here is how it is defined:
	 * Consider a segment [initialA, initialB] on a 1D axis. Let [newA, newB] be another segment on
	 * the same axis.
	 * Based on the initial length of the "initial" segment, the percentage of change is defined as
	 * the portion of the "new" segment that differs from the "initial" segment. This portion can
	 * either be an added part or a subtracted part.
	 * For instance, if the "new" segment is twice as large as the "initial" segment and completely
	 * encompasses it,	this percentage is 100% (because transitioning from "initial" to "new"
	 * represents a 100% increase).
	 * If the "new" segment is half the size of the "initial" segment and is contained within it,
	 * this percentage is 50% (since transitioning from "initial" to "new" reduces the size by 50%).
	 * If the "new" segment is the same size as the "initial" segment but shifted by 10% on one side,
	 * this percentage is 20% (as 10% is added on one shifted side and 10% is subtracted from the
	 * other side).
	 */
	function getMovePercentage1D(
		initialA: number,
		initialB: number,
		newA: number,
		newB: number,
	): number {
		const movePercentageA = Math.abs((initialA - newA) / (initialA - initialB)) * 100;
		const movePercentageB = Math.abs((initialB - newB) / (initialA - initialB)) * 100;
		return movePercentageA + movePercentageB;
	}

	function isBoundsMovePercentageBigEnough(
		treshold: number,
		initalBounds: BoundaryCoordinates,
		newBounds: BoundaryCoordinates,
	): boolean {
		const [[initialLowerLng, initialLowerLat], [initialUpperLng, initialUpperLat]] = initalBounds;
		const [[newLowerLng, newLowerLat], [newUpperLng, newUpperLat]] = newBounds;

		const lngMovePercentage = getMovePercentage1D(
			initialLowerLng,
			initialUpperLng,
			newLowerLng,
			newUpperLng,
		);
		const latMovePercentage = getMovePercentage1D(
			initialLowerLat,
			initialUpperLat,
			newLowerLat,
			newUpperLat,
		);

		if (lngMovePercentage > treshold || latMovePercentage > treshold) {
			return true;
		}
		return false;
	}

	function showRefreshButton() {
		const bounds = map?.getBounds().toArray() as BoundaryCoordinates | undefined;

		if (!bounds) {
			return;
		}

		if (areBoundsEqual(new LngLatBounds(initialFittedBounds), new LngLatBounds(bounds))) {
			$isRefreshButtonVisible = false;
			return;
		}

		$isRefreshButtonVisible = isBoundsMovePercentageBigEnough(10, mapBounds, bounds);
	}

	function refreshPois() {
		track({
			action: type === 'pois-hrt' ? 'see_pois_button' : 'searchagain_here_button',
			payload: { action: 'click' },
		});

		const bounds = map?.getBounds().toArray() as BoundaryCoordinates | undefined;
		if (bounds === undefined) {
			return;
		}

		onRefreshBounds?.(bounds);
		$isRefreshButtonVisible = false;
	}

	onMount(() => {
		$isRefreshButtonVisible = false;
		map?.on('moveend', showRefreshButton);
	});

	onDestroy(() => {
		map?.off('moveend', showRefreshButton);
	});

	const buttonPropsByType = {
		'search': {
			text: m.refresh_control__label(),
			icon: 'autorenew',
		},
		'pois-hrt': {
			text: m.launchpad__pois_hrt(),
			icon: 'visibility',
		},
	};
</script>

{#if $isRefreshButtonVisible}
	<div class="absolute top-4 z-0 translate-x--1/2 left-1/2 z-ontop bouncing-button">
		<Button
			variant="filled"
			color="dark"
			size="md"
			onclick={refreshPois}
			{...buttonPropsByType[type]}
		/>
	</div>
{/if}

<style>
	@keyframes bounceInDown {
		from,
		15%,
		30%,
		45%,
		60% {
			animation-timing-function: cubic-bezier(0.21, 1.34, 0.01, 0.98);
		}

		0% {
			transform: translate3d(-50%, -100%, 0);
			opacity: 0;
		}
		15% {
			opacity: 1;
			transform: translate3d(-50%, 15px, 0);
		}
		30% {
			transform: translate3d(-50%, -10px, 0);
		}
		45% {
			transform: translate3d(-50%, 5px, 0);
		}
		60% {
			transform: translate3d(-50%, 0, 0);
		}
	}

	.bouncing-button {
		animation: bounceInDown 600ms;
	}
</style>
